import { InvalidFieldsCount } from 'components/common-form/builder/form/node/block/components/InvalidFieldsCount';
import { FormattedMessage } from 'components/shared/Intl';
import { ConnectedPrecisionForm } from 'components/userPreferences/form/tabs/PrecisionForm.connect';
import React, { FC, MouseEvent, useCallback, useState } from 'react';
import { ListGroup, ListGroupItem } from 'react-bootstrap';
import styled from 'styled-components';

import { strictEntries } from './entries';
import { ClientTabForm, GeneralTabForm, ProductTabForm, SortTabForm } from './tabs';
import { NotificationsTabForm } from './tabs/NotificationsTabForm';
import { SecondaryMarketTabForm } from './tabs/SecondaryMarketTabForm';
import { UnderlyingTabForm } from './tabs/UnderlyingTabForm';
import type { UserPreferencesFormProps } from './UserPreferencesForm.connect';
import { connectUserPreferencesForm } from './UserPreferencesForm.connect';

type MenuItems = {
  general?: React.JSX.Element;
  client?: React.JSX.Element;
  products?: React.JSX.Element;
  tabsOrder?: React.JSX.Element;
  underlyings?: React.JSX.Element;
  secondaryMarket?: React.JSX.Element;
  notifications?: React.JSX.Element;
};

const internalUsersMenuItems: MenuItems = {
  general: <FormattedMessage id="userPreferencesForm.general.label" />,
  client: <FormattedMessage id="userPreferencesForm.client.label" />,
  products: <FormattedMessage id="userPreferencesForm.products.label" />,
  tabsOrder: <FormattedMessage id="userPreferencesForm.tabsOrder.label" />,
  underlyings: <FormattedMessage id="userPreferencesForm.underlyings.label" />,
  secondaryMarket: <FormattedMessage id="userPreferencesForm.secondaryMarket.label" />,
  notifications: <FormattedMessage id="userPreferencesForm.notifications.label" />,
};

const externalUsersMenuItems: MenuItems = {
  general: <FormattedMessage id="userPreferencesForm.general.label" />,
  products: <FormattedMessage id="userPreferencesForm.products.label" />,
  tabsOrder: <FormattedMessage id="userPreferencesForm.tabsOrder.label" />,
  underlyings: <FormattedMessage id="userPreferencesForm.underlyings.label" />,
  secondaryMarket: <FormattedMessage id="userPreferencesForm.secondaryMarket.label" />,
};
type UserPrefTab = keyof typeof internalUsersMenuItems;
const StyledDiv = styled.div`
  height: 600px;
`;

function getMenuEntries(
  isUserInternal: boolean,
  hasUnderlyingTypeCode: boolean,
): readonly [UserPrefTab, React.JSX.Element][] {
  const menuItems = isUserInternal ? internalUsersMenuItems : externalUsersMenuItems;

  const menuEntries = strictEntries<UserPrefTab, React.JSX.Element>(menuItems);
  if (!hasUnderlyingTypeCode) {
    return menuEntries.filter(([key]) => key !== 'underlyings');
  }
  return menuEntries;
}

export const RawUserPreferencesForm: FC<UserPreferencesFormProps> = ({
  onClose,
  isLoaded,
  landingPage,
  onLandingPageChanged,
  quoteWorkspaceVersion,
  onQuoteWorkspaceVersionChanged,
  batchWorkspaceVersion,
  onBatchWorkspaceVersionChanged,
  discoverPageRegion,
  onDiscoverPageRegionChanged,
  availableDiscoverPageRegions,
  isPrivateMode,
  productType,
  productSubtype,
  wrapper,
  currency,
  availableProductTypes,
  productTypeValidity,
  availableProductSubtypes,
  productSubTypeValidity,
  availableWrappers,
  wrapperValidity,
  availableCurrencies,
  currencyValidity,
  productTabErrorCount,
  onDefaultFieldChanged,
  clientSearchApi: { fetchSalesCounterparts },
  selectedOnBehalfClient,
  xMargin,
  isUserInternal,
  hasUnderlyingTypeCode,
  defaultUnderlyingCodeType,
  onUnderlyingCodeTypeChanged,
  onSpmTabsUpdated,
  onResetSpmOrder,
  hiddenSpmTabs,
  visibleSpmTabs,
  defaultProductSearchCodeType,
  onProductSearchTypeChanged,
  onAutomaticTabCloseChanged,
  closeTabWhenLiveTradeCpOrderIsPlacedActivated,
  onNotificationContactsChanged,
  notifications,
}) => {
  const [activeTab, setActiveTab] = useState<UserPrefTab>('general');

  const changeTab = useCallback(
    (tab: UserPrefTab) => (e: MouseEvent) => {
      e.preventDefault();
      setActiveTab(tab);
    },
    [],
  );

  const onClientSearch = useCallback(
    (text: any) => {
      return fetchSalesCounterparts(text);
    },
    [fetchSalesCounterparts],
  );

  const handleReset = () => {
    onResetSpmOrder();
    onClose();
  };

  if (!isLoaded) {
    return (
      <StyledDiv className="d-flex flex-column justify-content-center align-items-center">
        <div className="spinner-border mb-2" role="status"></div>
        <FormattedMessage id="userPreferencesForm.loading" />
      </StyledDiv>
    );
  }
  const menuEntries = getMenuEntries(isUserInternal, hasUnderlyingTypeCode);

  return (
    <StyledDiv className="d-flex">
      <div className="border-end" data-e2e="user-prefs-settings">
        <h3 className="p-3 px-md-4 text-truncate">
          <FormattedMessage id="userPreferencesForm.settings.label" />
        </h3>
        <ListGroup as="div">
          {menuEntries.map(([key, label]) => (
            <ListGroupItem as="a" key={key} action active={key === activeTab} onClick={changeTab(key)}>
              {label}
              {key === 'products' && (
                <InvalidFieldsCount
                  id="userPreferences-ProductTab"
                  value={{
                    totalCount: productTabErrorCount,
                    validations: [],
                    severity: 'error',
                  }}
                />
              )}
            </ListGroupItem>
          ))}
        </ListGroup>
      </div>
      <div className="flex-grow-1 overflow-auto h-100" data-e2e="user-prefs-options">
        {activeTab === 'general' && (
          <GeneralTabForm
            landingPage={landingPage}
            onLandingPageChanged={onLandingPageChanged}
            onClose={onClose}
            quoteWorkspaceVersion={quoteWorkspaceVersion}
            onQuoteWorkspaceVersionChanged={onQuoteWorkspaceVersionChanged}
            batchWorkspaceVersion={batchWorkspaceVersion}
            onBatchWorkspaceVersionChanged={onBatchWorkspaceVersionChanged}
            discoverPageRegion={discoverPageRegion}
            onDiscoverPageRegionChanged={onDiscoverPageRegionChanged}
            availableDiscoverPageRegions={availableDiscoverPageRegions}
          >
            <ConnectedPrecisionForm />
          </GeneralTabForm>
        )}
        {activeTab === 'client' && isUserInternal && (
          <ClientTabForm
            xMargin={xMargin ?? null}
            onClose={onClose}
            isPrivateMode={isPrivateMode}
            onDefaultFieldChanged={onDefaultFieldChanged}
            onClientSearch={onClientSearch}
            selectedOnBehalfClient={selectedOnBehalfClient ?? null}
          />
        )}
        {activeTab === 'products' && (
          <ProductTabForm
            onClose={onClose}
            productType={productType ?? null}
            productSubtype={productSubtype ?? null}
            wrapper={wrapper ?? null}
            currency={currency ?? null}
            availableProductTypes={availableProductTypes}
            productTypeValidity={productTypeValidity}
            availableProductSubtypes={availableProductSubtypes}
            productSubTypeValidity={productSubTypeValidity}
            availableWrappers={availableWrappers}
            wrapperValidity={wrapperValidity}
            availableCurrencies={availableCurrencies}
            currencyValidity={currencyValidity}
            onDefaultFieldChanged={onDefaultFieldChanged}
          />
        )}
        {activeTab === 'tabsOrder' && (
          <SortTabForm
            onClose={onClose}
            onSpmTabsUpdated={onSpmTabsUpdated}
            hiddenSpmTabs={hiddenSpmTabs}
            visibleSpmTabs={visibleSpmTabs}
            onReset={handleReset}
          />
        )}
        {activeTab === 'underlyings' && hasUnderlyingTypeCode && (
          <UnderlyingTabForm
            onClose={onClose}
            underlyingCodeType={defaultUnderlyingCodeType}
            onUnderlyingCodeTypeChanged={onUnderlyingCodeTypeChanged}
          />
        )}
        {activeTab === 'secondaryMarket' && (
          <SecondaryMarketTabForm
            onClose={onClose}
            isInternalUser={isUserInternal}
            defaultProductSearchCodeType={defaultProductSearchCodeType}
            onProductSearchTypeChanged={onProductSearchTypeChanged}
            onCloseTabWhenLiveTradeCpOrderIsPlacedChanged={onAutomaticTabCloseChanged}
            closeTabWhenLiveTradeCpOrderIsPlacedActivated={closeTabWhenLiveTradeCpOrderIsPlacedActivated}
          />
        )}
        {activeTab === 'notifications' && (
          <NotificationsTabForm
            onClose={onClose}
            onNotificationContactsChanged={onNotificationContactsChanged}
            notifications={notifications}
          />
        )}
      </div>
    </StyledDiv>
  );
};

const ConnectedUserPreferencesForm = connectUserPreferencesForm(RawUserPreferencesForm);

export default ConnectedUserPreferencesForm;
